import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2, Wallet, Receipt } from 'lucide-react';
import { expensesApi, sellersApi } from '../api';
import { Modal } from '../components/ui/Modal';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonCards } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { useAuthStore } from '../store/auth';
import { formatPrice } from '../store';
import type { Expense, ExpenseKind, ExpenseSummary, Seller } from '../types';

interface FormData {
  kind: ExpenseKind;
  seller: number | '';
  amount: string;
  reason: string;
  category: string;
}

const initialFormData: FormData = {
  kind: 'work',
  seller: '',
  amount: '',
  reason: '',
  category: '',
};

const KIND_LABEL: Record<ExpenseKind, string> = {
  work: 'Ish xarajati',
  advance: 'Avans (oylikdan)',
};

export const Expenses: React.FC = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const { permissions } = useAuthStore();

  const [items, setItems] = useState<Expense[]>([]);
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | ExpenseKind>('all');

  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  const canManage = summary?.can_manage ?? permissions.can_manage_expenses;
  const isAdmin = summary?.is_admin ?? permissions.is_admin;

  const asArray = <T,>(v: unknown): T[] => {
    if (Array.isArray(v)) return v as T[];
    if (v && typeof v === 'object' && Array.isArray((v as { results?: unknown }).results)) {
      return (v as { results: T[] }).results;
    }
    return [];
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? undefined : { kind: filter };
      const [listRes, summaryRes] = await Promise.all([
        expensesApi.list(params),
        expensesApi.summary(),
      ]);
      setItems(asArray<Expense>(listRes.data));
      setSummary(summaryRes.data);

    } catch (error) {
      console.error('Expenses fetch error:', error);
      toast.error('Chiqimlarni yuklashda xatolik');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!permissions.is_admin) return;
    sellersApi.list()
      .then((res) => setSellers(asArray<Seller>(res.data)))
      .catch(() => {});
  }, [permissions.is_admin]);

  const openModal = () => {
    setFormData(initialFormData);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await expensesApi.create({
        kind: formData.kind,
        seller: formData.seller || undefined,
        amount: formData.amount,
        reason: formData.reason.trim(),
        category: formData.category.trim(),
      });
      toast.success('Chiqim yozildi');
      setShowModal(false);
      fetchData();
    } catch (error: any) {
      const data = error.response?.data;
      const msg = data?.error || data?.seller || data?.amount || data?.reason || data?.detail || 'Saqlashda xato';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: Expense) => {
    const confirmed = await confirm({
      title: "Yozuvni o'chirish",
      description: `"${item.reason}" — ${formatPrice(item.amount)} so'm yozuvini o'chirmoqchimisiz?`,
      confirmLabel: "O'chirish",
      danger: true,
    });
    if (!confirmed) return;

    setDeleting(item.id);
    try {
      await expensesApi.delete(item.id);
      toast.success("Yozuv o'chirildi");
      fetchData();
    } catch (error) {
      toast.error("O'chirishda xato");
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="page-bar flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Kassa chiqimlari</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Ish xarajati do'kondan, avans esa sotuvchining oyligidan ayriladi
          </p>
        </div>
        {canManage && (
          <button onClick={openModal} className="btn btn-primary flex items-center gap-2">
            <Plus size={20} />
            <span>Yangi chiqim</span>
          </button>
        )}
      </div>

      {/* Bu oy bo'yicha jamlar — faqat adminda */}
      {isAdmin && summary?.total !== undefined && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="card">
            <p className="text-xs sm:text-sm text-gray-500">Bu oy — ish xarajati</p>
            <p className="text-lg sm:text-2xl font-bold text-gray-900">
              {formatPrice(summary.work_total || 0)} so'm
            </p>
          </div>
          <div className="card">
            <p className="text-xs sm:text-sm text-gray-500">Bu oy — avans</p>
            <p className="text-lg sm:text-2xl font-bold text-warning-600">
              {formatPrice(summary.advance_total || 0)} so'm
            </p>
          </div>
          <div className="card">
            <p className="text-xs sm:text-sm text-gray-500">Kassadan jami chiqim</p>
            <p className="text-lg sm:text-2xl font-bold text-danger-600">
              {formatPrice(summary.total || 0)} so'm
            </p>
          </div>
        </div>
      )}

      {/* Oylik holati */}
      {summary && summary.sellers.length > 0 && (
        <div className="card !p-0">
          <div className="flex items-center gap-2 p-4 pb-2">
            <Wallet size={18} className="text-primary-800" />
            <h2 className="text-lg font-semibold text-gray-900">Oylik holati</h2>
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Sotuvchi</th>
                  <th className="text-right py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Oylik</th>
                  <th className="text-right py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Olingan avans</th>
                  <th className="text-right py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qoldiq</th>
                  <th className="text-left py-2 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Davr</th>
                </tr>
              </thead>
              <tbody>
                {summary.sellers.map((s) => (
                  <tr key={s.seller_id} className="border-b border-gray-100 last:border-0">
                    <td className="py-2.5 px-4 font-medium">{s.seller_name}</td>
                    <td className="py-2.5 px-4 text-right">
                      {Number(s.monthly_salary) > 0
                        ? `${formatPrice(s.monthly_salary)} so'm`
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2.5 px-4 text-right text-warning-600 font-medium">
                      {formatPrice(s.advance_taken)} so'm
                    </td>
                    <td className="py-2.5 px-4 text-right font-bold">
                      {s.remaining !== null
                        ? <span className={Number(s.remaining) < 0 ? 'text-danger-600' : 'text-success-600'}>
                            {formatPrice(s.remaining)} so'm
                          </span>
                        : <span className="text-gray-400">Oylik belgilanmagan</span>}
                    </td>
                    <td className="py-2.5 px-4 text-sm text-gray-500">
                      {s.period_start} — {s.period_end}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sm:hidden divide-y divide-gray-100">
            {summary.sellers.map((s) => (
              <div key={s.seller_id} className="p-4 space-y-1">
                <div className="flex justify-between gap-2">
                  <span className="font-medium truncate min-w-0">{s.seller_name}</span>
                  <span className="text-sm text-gray-500 whitespace-nowrap shrink-0">{s.payday_day}-sana</span>
                </div>
                <div className="flex justify-between gap-2 text-sm">
                  <span className="text-gray-500 shrink-0">Oylik</span>
                  <span className="whitespace-nowrap">{Number(s.monthly_salary) > 0 ? `${formatPrice(s.monthly_salary)} so'm` : '—'}</span>
                </div>
                <div className="flex justify-between gap-2 text-sm">
                  <span className="text-gray-500 shrink-0">Olingan avans</span>
                  <span className="text-warning-600 font-medium whitespace-nowrap">{formatPrice(s.advance_taken)} so'm</span>
                </div>
                <div className="flex justify-between gap-2 text-sm">
                  <span className="text-gray-500 shrink-0">Qoldiq</span>
                  {s.remaining !== null ? (
                    <span className={`font-bold whitespace-nowrap ${Number(s.remaining) < 0 ? 'text-danger-600' : 'text-success-600'}`}>
                      {formatPrice(s.remaining)} so'm
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtr */}
      <div className="flex gap-2">
        {([['all', 'Hammasi'], ['work', 'Ish xarajati'], ['advance', 'Avans']] as const).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
              filter === k ? 'bg-primary-50 text-primary-800' : 'bg-white text-gray-500 hover:bg-gray-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Yozuvlar */}
      {loading ? (
        <SkeletonCards />
      ) : items.length === 0 ? (
        <div className="card">
          <EmptyState icon={Receipt} title="Yozuv topilmadi" />
        </div>
      ) : (
        <div className="card !p-0 divide-y divide-gray-100">
          {items.map((item) => (
            <div key={item.id} className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{item.reason}</span>
                  <StatusBadge tone={item.kind === 'advance' ? 'warning' : 'neutral'}>
                    {KIND_LABEL[item.kind]}
                  </StatusBadge>
                  {item.category && (
                    <span className="text-xs text-gray-400">{item.category}</span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {item.seller_name} · {new Date(item.created_at).toLocaleString('uz-UZ', {
                    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                  {item.created_by_name && item.created_by_name !== item.seller_name
                    ? ` · yozdi: ${item.created_by_name}`
                    : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-bold whitespace-nowrap">{formatPrice(item.amount)} so'm</span>
                {canManage && (
                  <button
                    onClick={() => handleDelete(item)}
                    disabled={deleting === item.id}
                    className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg disabled:opacity-50"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        title="Yangi chiqim"
        size="sm"
        footer={
          <>
            <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">
              Bekor qilish
            </button>
            <button type="submit" form="expense-form" disabled={saving} className="btn btn-primary">
              {saving ? 'Saqlanmoqda...' : 'Saqlash'}
            </button>
          </>
        }
      >
        <form id="expense-form" onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Turi *</label>
            <div className="grid grid-cols-2 gap-2">
              {(['work', 'advance'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setFormData({ ...formData, kind: k })}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                    formData.kind === k
                      ? 'border-primary-600 bg-primary-50 text-primary-800'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {KIND_LABEL[k]}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {formData.kind === 'work'
                ? "Suv, ovqat, hojatxona — do'kon xarajati, oylikka tegmaydi"
                : 'Sotuvchi o\'zi uchun oldi — oy oxirida oyligidan ayriladi'}
            </p>
          </div>

          {isAdmin && (
            <div>
              <label className="label">Kim oldi</label>
              <select
                className="input"
                value={formData.seller}
                onChange={(e) => setFormData({ ...formData, seller: e.target.value ? Number(e.target.value) : '' })}
              >
                <option value="">O'zim</option>
                {sellers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="label">Summa (so'm) *</label>
            <input
              type="number"
              min="1"
              step="any"
              className="input"
              placeholder="400000"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Nimaga *</label>
            <input
              type="text"
              className="input"
              placeholder="Masalan: suvga, dori, yo'lkira"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              required
            />
          </div>

          <div>
            <label className="label">Kategoriya</label>
            <input
              type="text"
              className="input"
              placeholder="Ixtiyoriy — hisobotda guruhlash uchun"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};
